import { SLOT_RESOLUTION_REASON, type SlotResolutionReason } from "../resolvers/slot-resolution.js";

/**
 * 🔴 US-AIV2-0023d (@UC0226) — CÂU nói cho "phạm vi rỗng VÌ BỊ CHẶN", giữ ở Sổ, đúng MỘT bản.
 *
 * Vì sao ở đây chứ không ở ô (`AiSlot.emptyAsk`) và cũng không ở engine:
 *  · `emptyAsk` là câu của Ô ("nhóm chưa có quỹ nào đang hoạt động" · "chưa có đợt thu nào đang mở") — nó trả lời
 *    *"không có gì để chọn"*. Nguyên nhân **quỹ bị đóng băng** thì GIỐNG NHAU ở mọi ô và mọi trang, nên nhét nó
 *    vào từng ô là chép cùng một câu nhiều lần rồi để chúng trôi khỏi nhau.
 *  · Engine tự viết câu = "tầng trình bày bịa câu" — điều repo cấm. Engine chỉ TRA bảng này theo lý do.
 *
 * 🔴 `cause` KHÔNG phải trang trí (review #860 M3). Lưới cũ chỉ chặn *bản sao y nguyên* của `emptyAsk`, nên một câu
 * MỚI mà VẪN sai nguyên nhân ("Bạn chưa có quỹ nào phù hợp…") vẫn xanh — đúng thứ task này sinh ra để chặn. Nay mỗi
 * lý do phải khai TỪ CHỈ NGUYÊN NHÂN, câu phải chứa nó, và từ đó **không được xuất hiện trong bất kỳ `emptyAsk` nào
 * của Sổ** (nếu có thì nó không phân biệt được "bị chặn" với "chưa có" — tức không phải từ chỉ nguyên nhân).
 *
 * 🔴 Câu KHÔNG mời một hành động, và đây là sửa theo review #860 M4. Đo được trên cây: `funds.status` chỉ đổi ở MỘT
 * chỗ — `SecurityIntervene._freezeFund` (`active → frozen`, status-guarded, cửa admin hệ thống) — và `SECURITY_INTERVENE_KIND`
 * **không có** `unfreeze`; admin portal chỉ LIỆT KÊ quỹ frozen, 0 nút. Nghĩa là **kể cả OPS/SUPER_ADMIN cũng không mở
 * khoá được** bằng đường sản phẩm. Bản trước của file này viết *"liên hệ hỗ trợ Goppy để mở lại quỹ"* ⇒ chỉ đường tới
 * một bên **cũng không làm được** — đúng thứ CLAUDE.md §4 cấm: *đừng hứa một quyền hệ thống không thực hiện được*.
 * Nói SỰ THẬT, không kèm lời hứa. Muốn câu có lối ra thì phải dựng `unfreeze_fund` trước, rồi mới đổi câu.
 */
interface ScopeBlockedText {
  /** Từ chỉ NGUYÊN NHÂN — phải có trong `text`, và không được trùng chữ của bất kỳ `emptyAsk` nào (test ép). */
  readonly cause: string;
  readonly text: string;
}

const EMPTY_SCOPE_TEXT: Readonly<Partial<Record<SlotResolutionReason, ScopeBlockedText>>> = Object.freeze({
  [SLOT_RESOLUTION_REASON.SCOPE_FUND_FROZEN]: Object.freeze({
    cause: "đóng băng",
    text: "Quỹ đang bị đóng băng nên mình chưa làm tiếp được việc này.",
  }),
});

/**
 * Các lý do RỖNG-VÌ-BỊ-CHẶN — suy TỪ bảng câu, không khai lại bằng tay (hai danh sách cạnh nhau thì trôi).
 * `empty_scope` chung KHÔNG nằm ở đây: ô vẫn dùng `emptyAsk` của chính nó, kèm nút sang trang tạo.
 */
export const AI_SCOPE_BLOCKED_REASONS: readonly SlotResolutionReason[] = Object.freeze(
  Object.keys(EMPTY_SCOPE_TEXT) as SlotResolutionReason[],
);

/** Câu của Sổ cho một lý do rỗng; `undefined` = lý do đó không có câu riêng (⇒ caller dùng `emptyAsk` của ô). */
export function emptyScopeText(reason: SlotResolutionReason): string | undefined {
  return EMPTY_SCOPE_TEXT[reason]?.text;
}

/** Từ chỉ nguyên nhân mà câu của lý do đó BẮT BUỘC phải nêu — để lưới kiểm được "câu có nói đúng nguyên nhân không". */
export function emptyScopeCause(reason: SlotResolutionReason): string | undefined {
  return EMPTY_SCOPE_TEXT[reason]?.cause;
}
